import { config } from '@portal/shared/config';
import { RudderAnalytics } from '@rudderstack/analytics-js';
import { RudderIdentifierPayload, RudderStitchOperation, CartDetails } from './types';
import { getTokenFromCartId } from '@portal/shared/utils/format/parseCartId';
import { StoreEnum } from 'schema/storeCode';
import cookies from 'js-cookie';

export function getStitchingConfig() {
  const storeCode = (cookies.get('ag-store') as StoreEnum) || StoreEnum.us;

  switch (storeCode) {
    case StoreEnum.us:
      return config.NEXT_PUBLIC_RUDDERSTACK_IDENTITY_STITCHING_WRITE_KEY_US;
    case StoreEnum.eu:
      return config.NEXT_PUBLIC_RUDDERSTACK_IDENTITY_STITCHING_WRITE_KEY_EU;
    case StoreEnum.uk:
      return config.NEXT_PUBLIC_RUDDERSTACK_IDENTITY_STITCHING_WRITE_KEY_UK;
    default:
      return config.NEXT_PUBLIC_RUDDERSTACK_IDENTITY_STITCHING_WRITE_KEY_US;
  }
}

async function postSendStitchingRequest(data: RudderIdentifierPayload) {
  try {
    const writeKey = getStitchingConfig();

    const response = await fetch(
      `${config.NEXT_PUBLIC_RUDDERSTACK_IDENTITY_STITCHING_DATA_PLANE_URL}/v1/webhook?writeKey=${writeKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    );

    if (!response.ok) {
      throw new Error(`RudderStack stitching request failed: ${response.status}`);
    }
  } catch (err) {
    console.error('Failed to stitch user or cart identity:', err);
  }
}

export async function stitchCartTokenToAnonymousId(
  anonymousId: string,
  cartToken: string,
  cart: CartDetails
) {
  if (!anonymousId || !cartToken) return;

  const data: RudderIdentifierPayload = {
    event: 'rudderIdentifier',
    pixelEventLabel: true,
    anonymousId,
    action: 'stitchCartTokenToAnonId',
    cartToken,
    cart
  };

  await postSendStitchingRequest(data);
}

export async function stitchUserIdToAnonymousId(anonymousId: string, userId: string) {
  if (!anonymousId || !userId) return;

  const data: RudderIdentifierPayload = {
    event: 'rudderIdentifier',
    pixelEventLabel: true,
    anonymousId,
    action: 'stitchUserIdToAnonId',
    userId
  };

  await postSendStitchingRequest(data);
}

export async function rudderstackIdentityStitching(operation: RudderStitchOperation) {
  // Only run in production
  if (process.env.NODE_ENV !== 'production') {
    return;
  }

  const rudderAnalytics = RudderAnalytics.globalSingleton;
  if (!rudderAnalytics) return;

  try {
    const anonymousId = rudderAnalytics.getAnonymousId();
    if (!anonymousId) return;

    if (operation.type === 'cartIdentity') {
      const cartToken = getTokenFromCartId(operation.cart?.id);
      if (!cartToken) return;

      await stitchCartTokenToAnonymousId(anonymousId, cartToken, operation.cart);
    }

    if (operation.type === 'userIdentity') {
      const userId = operation?.userId?.split('/').filter(Boolean).pop();
      if (!userId) return;

      await stitchUserIdToAnonymousId(anonymousId, userId);
    }
  } catch (err) {
    console.error(`Rudderstack ${operation.type} stitching failed:`, err);
  }
}
